import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

const footerLinks = [
  { label: 'Hipobuy Spreadsheet Guide', to: '/hipobuy-spreadsheet/' },
  { label: 'Kakobuy Spreadsheet', to: '/kakobuy-spreadsheet/' },
  { label: 'CSSBuy Spreadsheet', to: '/cssbuy-spreadsheet/' },
  { label: 'Superbuy Spreadsheet', to: '/superbuy-spreadsheet/' },
  { label: 'Oopbuy Spreadsheet', to: '/oopbuy-spreadsheet/' },
  { label: 'Complete Comparison', to: '/hipobuy-spreadsheet-comparison/' },
  { label: 'Safety Guide', to: '/hipobuy-spreadsheet-safety/' },
  { label: 'Categories Guide', to: '/hipobuy-spreadsheet-categories/' },
];

export default function Footer() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d99labig3iubr452incg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      const isSpam = serverMsg.toLowerCase().includes('spam') || responseText.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="bg-[#1a2e25] text-white">
      <div className="w-full px-4 md:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 max-w-[1280px] mx-auto">
          <div>
            <Link to="/" className="font-heading text-xl font-bold tracking-tight">
              Hipobuy Sheet
            </Link>
            <p className="mt-3 text-white/40 text-sm leading-relaxed max-w-xs">
              The most comprehensive Hipobuy Spreadsheet resource hub for product research, category discovery and safe shopping education.
            </p>
          </div>

          <div>
            <h4 className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-4">
              SEO Guides
            </h4>
            <div className="flex flex-col gap-2.5">
              {footerLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className="text-white/70 text-sm hover:text-white transition-colors duration-150"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-4">
              Categories
            </h4>
            <div className="flex flex-col gap-2.5">
              {categories.slice(0, 7).map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-white/70 text-sm hover:text-white transition-colors duration-150"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-4">
              Stay Updated
            </h4>
            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="flex items-center border-b border-white/20 pb-2"
            >
              <input
                type="email"
                name="email"
                placeholder="Your email"
                className="bg-transparent text-white text-sm placeholder-white/30 outline-none flex-1"
                required
              />
              <input
                type="text"
                name="phone_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="text-white/50 hover:text-white transition-colors duration-150 cursor-pointer disabled:opacity-30 flex-shrink-0"
              >
                <i className="ri-arrow-right-line"></i>
              </button>
            </form>
            {formStatus === 'success' && (
              <p className="mt-2 text-accent-300 text-xs">Thanks for subscribing!</p>
            )}
            {formStatus === 'error' && (
              <p className="mt-2 text-red-400 text-xs">{formError}</p>
            )}
            {formStatus === 'idle' && (
              <p className="mt-2 text-white/30 text-xs">
                Get the latest spreadsheet updates
              </p>
            )}
            <div className="mt-6 flex items-center gap-4">
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-white/40 hover:text-white/70 transition-colors duration-150" aria-label="Main website">
                <i className="ri-global-line text-lg"></i>
              </a>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-white/40 hover:text-white/70 transition-colors duration-150" aria-label="Twitter">
                <i className="ri-twitter-x-line text-lg"></i>
              </a>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-white/40 hover:text-white/70 transition-colors duration-150" aria-label="Pinterest">
                <i className="ri-pinterest-line text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/[0.06]">
        <div className="w-full px-4 md:px-10 py-10 flex items-center justify-center overflow-hidden">
          <span className="text-[#2a4538] text-6xl md:text-8xl lg:text-[10rem] font-heading font-bold tracking-tighter leading-none select-none">
            HIPOBUY
          </span>
        </div>
      </div>

      <div className="border-t border-white/[0.06]">
        <div className="w-full px-4 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 max-w-[1280px] mx-auto">
          <p className="text-white/30 text-xs">
            &copy; {new Date().getFullYear()} Hipobuy Spreadsheet. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-white/30 text-xs hover:text-white/50 transition-colors duration-150">
              Privacy
            </a>
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-white/30 text-xs hover:text-white/50 transition-colors duration-150">
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}